/**
 * Chrome for the server-administration section (CLAUDE.md 12, 12.2).
 *
 * Deliberately *not* `WorkspaceChrome`. Server administration is not a business role
 * (11.2) and has no active workspace, so the workspace switcher — the one control that
 * chrome exists to provide — would be meaningless here and actively misleading: it
 * would suggest that what this page shows is scoped to whatever workspace happened to
 * be selected. It is not; it is the whole instance.
 *
 * Responsive per 14.3: a sidebar at `lg` and above, a horizontally scrollable tab strip
 * below it. Every table on the pages below scrolls inside its own container so the page
 * body never scrolls sideways.
 */
import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import {
  Blocks,
  Gauge,
  KeyRound,
  LayoutTemplate,
  LifeBuoy,
  ScrollText,
  Settings2,
  Users,
  Building2,
  ArrowLeft,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { UserMenu } from '@/components/layout/UserMenu'

export const ADMIN_SECTIONS = [
  { id: 'dashboard', path: '/admin', icon: Gauge, labelKey: 'admin.nav.dashboard' },
  { id: 'users', path: '/admin/users', icon: Users, labelKey: 'admin.nav.users' },
  {
    id: 'workspaces',
    path: '/admin/workspaces',
    icon: Building2,
    labelKey: 'admin.nav.workspaces',
  },
  { id: 'sessions', path: '/admin/sessions', icon: KeyRound, labelKey: 'admin.nav.sessions' },
  { id: 'settings', path: '/admin/settings', icon: Settings2, labelKey: 'admin.nav.settings' },
  { id: 'modules', path: '/admin/modules', icon: Blocks, labelKey: 'admin.nav.modules' },
  {
    id: 'sheet-templates',
    path: '/admin/sheet-templates',
    icon: LayoutTemplate,
    labelKey: 'admin.nav.sheet_templates',
  },
  { id: 'tickets', path: '/admin/tickets', icon: LifeBuoy, labelKey: 'admin.nav.tickets' },
  { id: 'audit', path: '/admin/audit', icon: ScrollText, labelKey: 'admin.nav.audit' },
] as const

export type AdminSectionId = (typeof ADMIN_SECTIONS)[number]['id']

export function AdminShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['admin', 'common'])

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface-raised focus:px-3 focus:py-2"
      >
        {t('nav.skip_to_content', { ns: 'common' })}
      </a>

      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-3 sm:px-4">
        <Link
          to="/projects"
          className="flex h-11 items-center gap-2 rounded-md px-2 text-sm text-text-muted hover:text-text"
        >
          <ArrowLeft size={15} aria-hidden />
          <span className="hidden sm:inline">{t('nav.projects', { ns: 'common' })}</span>
        </Link>
        <span className="truncate font-semibold">{t('admin.title')}</span>

        <div className="ms-auto flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      {/* Below `lg` the sidebar becomes a scrollable strip. It keeps its own
          overflow container so the page body never scrolls horizontally (14.3). */}
      <nav
        className="shrink-0 overflow-x-auto border-b border-border bg-surface lg:hidden"
        aria-label={t('admin.title')}
      >
        <div className="flex w-max gap-1 px-2 py-1.5">
          {ADMIN_SECTIONS.map(({ id, path, icon: Icon, labelKey }) => (
            <NavLink
              key={id}
              to={path}
              end={path === '/admin'}
              className={({ isActive }) =>
                cn(
                  'flex h-11 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm',
                  isActive ? 'bg-surface-raised text-text' : 'text-text-muted hover:text-text',
                )
              }
            >
              <Icon size={15} aria-hidden />
              {t(labelKey)}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-60 shrink-0 border-e border-border bg-surface p-2 lg:block">
          <p className="px-3 pb-2 pt-1 text-xs text-text-muted">{t('admin.subtitle')}</p>
          {ADMIN_SECTIONS.map(({ id, path, icon: Icon, labelKey }) => (
            <NavLink
              key={id}
              to={path}
              end={path === '/admin'}
              className={({ isActive }) =>
                cn(
                  'flex h-11 items-center gap-2 rounded-md px-3 text-sm',
                  isActive ? 'bg-surface-raised text-text' : 'text-text-muted hover:text-text',
                )
              }
            >
              <Icon size={15} aria-hidden />
              {t(labelKey)}
            </NavLink>
          ))}
        </aside>

        <main id="main" className="min-w-0 flex-1 p-3 sm:p-4">
          {children}
        </main>
      </div>
    </div>
  )
}

/* --- shared table primitives ------------------------------------------------------ */

/** A table that scrolls inside itself. Wide data must never widen the page (14.3). */
export function TableScroller({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[40rem] text-sm">{children}</table>
    </div>
  )
}

export function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-border bg-surface px-3 py-2 text-start text-xs font-semibold text-text-muted',
        className,
      )}
    >
      {children}
    </th>
  )
}

export function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn('border-b border-border px-3 py-2 align-top', className)}>{children}</td>
}

export function Pagination({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number
  limit: number
  offset: number
  onChange: (offset: number) => void
}) {
  const { t } = useTranslation('admin')
  if (total <= limit) return null

  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)

  return (
    <div className="flex items-center justify-between gap-2 pt-3 text-sm">
      <span className="text-text-muted">{t('admin.pagination.range', { from, to, total })}</span>
      <div className="flex gap-2">
        <button
          type="button"
          className="h-11 rounded-md border border-border px-3 disabled:opacity-50"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          {t('admin.pagination.previous')}
        </button>
        <button
          type="button"
          className="h-11 rounded-md border border-border px-3 disabled:opacity-50"
          disabled={to >= total}
          onClick={() => onChange(offset + limit)}
        >
          {t('admin.pagination.next')}
        </button>
      </div>
    </div>
  )
}

/** Status text that never relies on colour alone (14.2). */
export function Pill({
  tone,
  children,
}: {
  tone: 'pass' | 'warn' | 'fail' | 'muted'
  children: ReactNode
}) {
  const colour = {
    pass: 'text-[var(--status-pass)] border-[var(--status-pass)]',
    warn: 'text-[var(--status-warn)] border-[var(--status-warn)]',
    fail: 'text-[var(--status-fail)] border-[var(--status-fail)]',
    muted: 'text-text-muted border-border',
  }[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs whitespace-nowrap',
        colour,
      )}
    >
      {children}
    </span>
  )
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['kB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let index = 0
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }
  // ADR-0004: "." decimal separator in every locale, so this is `toFixed`, not
  // `toLocaleString`.
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[index]}`
}

export function formatDateTime(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  // ISO-8601, trimmed to the minute: an audit trail is read for ordering, and a
  // locale-formatted timestamp is ambiguous across the two launch languages.
  return date.toISOString().slice(0, 16).replace('T', ' ')
}
